var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useMemo } from 'react';
import propTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { Modal } from '@johnlewispartnership/wtr-ingredients/dist/Modal';
import Button from '@johnlewispartnership/wtr-ingredients/dist/Button';
import { Typography } from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import usePrevious from '../../utils/hooks/use-previous';
import { formatAsLongDate } from '../../utils/dates/format';
import AddressSection from '../../components/CompetitionsForms/Sections/Address';
import ClosedCompetition from '../../components/CompetitionsForms/Sections/Closed';
import DetailsSection from '../../components/CompetitionsForms/Sections/Details';
import ErrorSummary from '../../components/CompetitionsForms/Sections/ErrorSummary';
import MyWaitroseDetails from '../../components/CompetitionsForms/Sections/MyWaitrose';
import OptInSection from '../../components/CompetitionsForms/Sections/OptIn';
import QuestionAndAnswers from '../../components/CompetitionsForms/Sections/QuestionAndAnswers';
import ReceiptSection from '../../components/CompetitionsForms/Sections/Receipt';
import SubmissionSuccess from '../../components/CompetitionsForms/Sections/SubmissionSuccess';
import SimpleQuestion from '../../components/CompetitionsForms/Sections/SimpleQuestion';
import analyticsPropType from '../../constants/data-shapes/analytics';
import deviceBreakpointsProptypes from '../../constants/data-shapes/device-breakpoints';
import { richTextNode } from '../../constants/types/rich-text';
import isForbidden from '../../components/CompetitionsForms/is-forbidden';
import { CROSSWORD, MY_WAITROSE, RECIEPT_REQUIRED } from '../../constants/competition-forms';
import TermsAndConditions from './Sections/TermsAndConditions';
import styles from './CompetitionsForm.module.scss';
const compileUserDetailsForLoyalty = (contactAddress, emailAddress) => {
    if (!contactAddress || !emailAddress) {
        return null;
    }
    const { addressee: { firstName, lastName, title, contactNumber }, } = contactAddress;
    const fullName = [firstName, lastName].join(' ');
    const phoneNumber = contactNumber || '';
    if (title && firstName && lastName) {
        return {
            title,
            fullName,
            email: emailAddress,
            phone: phoneNumber,
        };
    }
    return null;
};
const CompetitionForm = ({ breakpointClasses = [], competitionEndDate, competitionId, competitionStatus, competitionType, competitionQuestion, competitionName, onFormSubmit, questionAndOptions, optIns, useGoogleReCaptcha, competitionsSingleClickEntry, userIsLoggedIn, membershipCardNumber, contactAddress, emailAddress, }) => {
    const form = useForm({ shouldFocusError: false });
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { errors, formState: { isSubmitSuccessful, isSubmitted, isSubmitting }, handleSubmit, setError, clearErrors, } = form;
    const errorsCount = Object.entries(errors).length;
    const previousErrorsCount = usePrevious(errorsCount);
    const showRejectedModal = isForbidden(errors);
    const successRef = useRef();
    const summaryRef = useRef();
    const shouldAutoFill = competitionsSingleClickEntry && competitionType === MY_WAITROSE && userIsLoggedIn && form;
    const userDetails = useMemo(() => compileUserDetailsForLoyalty(contactAddress, emailAddress), [contactAddress, emailAddress]);
    const autoFilledCard = shouldAutoFill && membershipCardNumber ? { loyaltyCardNumber: membershipCardNumber } : null;
    const autoFilledDetails = shouldAutoFill && userDetails ? userDetails : null;
    const autoFilled = !!autoFilledCard && !!autoFilledDetails;
    useEffect(() => {
        var _a;
        if (errorsCount > previousErrorsCount) {
            (_a = summaryRef === null || summaryRef === void 0 ? void 0 : summaryRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [previousErrorsCount, errorsCount]);
    useEffect(() => {
        var _a;
        if (isSubmitted && isSubmitSuccessful) {
            (_a = successRef === null || successRef === void 0 ? void 0 : successRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [isSubmitted, isSubmitSuccessful]);
    if (isSubmitted && isSubmitSuccessful) {
        return (React.createElement("div", { ref: successRef, className: classNames(breakpointClasses) },
            React.createElement(SubmissionSuccess, { competitionEndDate: competitionEndDate })));
    }
    if (competitionStatus === 'ENDED') {
        return (React.createElement("div", { className: classNames(breakpointClasses) },
            React.createElement(ClosedCompetition, null)));
    }
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        const token = yield executeRecaptcha('login_page');
        const submitData = Object.assign(Object.assign(Object.assign({}, data), (autoFilledCard !== null && autoFilledCard !== void 0 ? autoFilledCard : {})), (autoFilledDetails !== null && autoFilledDetails !== void 0 ? autoFilledDetails : {}));
        try {
            yield onFormSubmit(submitData, competitionType, competitionId, token);
        }
        catch (err) {
            if (err.status === 403) {
                setError('forbidden', {
                    type: 'forbidden',
                    message: 'response forbidden',
                });
                return;
            }
            setError('email', {
                type: 'duplicate-entry',
                message: 'You have already entered this competition',
            });
        }
    });
    const submitSection = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classNames(styles.spacer, styles.sectionWrapper) },
            React.createElement(Button, { id: "submit-button", label: autoFilled ? 'Enter now' : 'Submit your entry', type: "submit", width: "full", waiting: isSubmitting, theme: autoFilled ? 'finalising' : 'primary' })),
        React.createElement("div", { className: classNames(styles.spacer, styles.sectionWrapper) },
            React.createElement(Typography, { styleAs: "paragraph", element: "p" },
                "The closing date for competitions is ",
                formatAsLongDate(competitionEndDate)))));
    const modal = (React.createElement(Modal, { buttons: [
            {
                buttonText: 'OK, understood',
                secondary: true,
            },
        ], handleModalClose: () => {
            clearErrors('forbidden');
        }, dataTest: "competitions-rejected-modal", isOpen: showRejectedModal, messageText: [
            'Sorry, something has gone wrong. These issues are normally temporary. Please wait a few moments and resubmit your entry.',
        ], titleText: "There's been an error" }));
    if (competitionType === CROSSWORD) {
        return (React.createElement("div", { id: "competition-form-wrapper", className: classNames(styles.wrapper, breakpointClasses), "data-testid": "crossword-competition" },
            modal,
            React.createElement("form", { onSubmit: handleSubmit(onSubmit) },
                React.createElement("div", { className: classNames(styles.spacer, styles.sectionWrapper) },
                    React.createElement(Typography, { styleAs: "sectionHeadingLight", element: "h3" }, competitionName)),
                React.createElement(ErrorSummary, { ref: summaryRef, errorsCount: errorsCount, errors: errors }),
                React.createElement(QuestionAndAnswers, { form: form, questionAndOptions: questionAndOptions }),
                React.createElement(DetailsSection, { form: form }),
                React.createElement(AddressSection, { form: form }),
                React.createElement(TermsAndConditions, { form: form }),
                React.createElement(OptInSection, { form: form, optIns: optIns }),
                submitSection)));
    }
    if (competitionType === MY_WAITROSE) {
        return (React.createElement("div", { id: "competition-form-wrapper", className: classNames(styles.wrapper, breakpointClasses), "data-testid": "mywaitrose-competition" },
            modal,
            React.createElement("form", { onSubmit: handleSubmit(onSubmit) },
                autoFilled && (React.createElement("div", { className: classNames(styles.spacer, styles.sectionWrapper) },
                    React.createElement(Typography, { styleAs: "sectionHeadingLight", element: "h3" }, "Submit your entry"))),
                !!autoFilledCard || (React.createElement("div", { className: classNames(styles.spacer, styles.sectionWrapper) },
                    React.createElement(Typography, { styleAs: "sectionHeadingLight", element: "h3" }, "Your myWaitrose details"))),
                React.createElement(ErrorSummary, { ref: summaryRef, errorsCount: errorsCount, errors: errors }),
                !!autoFilledCard || React.createElement(MyWaitroseDetails, { form: form }),
                !!autoFilledDetails || React.createElement(DetailsSection, { form: form }),
                React.createElement(TermsAndConditions, { form: form, singleClickEntry: autoFilled }),
                React.createElement(OptInSection, { form: form, optIns: optIns, singleClickEntry: autoFilled }),
                submitSection)));
    }
    if (competitionType === RECIEPT_REQUIRED) {
        return (React.createElement("div", { id: "competition-form-wrapper", className: classNames(styles.wrapper, breakpointClasses), "data-testid": "receiptrequired-competition" },
            modal,
            React.createElement("form", { onSubmit: handleSubmit(onSubmit) },
                React.createElement("div", { className: classNames(styles.spacer, styles.sectionWrapper) },
                    React.createElement(Typography, { styleAs: "sectionHeadingLight", element: "h3" }, "Purchase details")),
                React.createElement(ErrorSummary, { ref: summaryRef, errorsCount: errorsCount, errors: errors }),
                React.createElement(ReceiptSection, { form: form }),
                React.createElement(SimpleQuestion, { form: form, competitionQuestion: competitionQuestion }),
                React.createElement(DetailsSection, { form: form }),
                React.createElement(AddressSection, { form: form }),
                React.createElement(TermsAndConditions, { form: form }),
                React.createElement(OptInSection, { form: form, optIns: optIns }),
                submitSection)));
    }
    return (React.createElement("div", { id: "competition-form-wrapper", className: classNames(styles.wrapper, breakpointClasses), "data-testid": "receiptnotrequired-competition" },
        modal,
        React.createElement("form", { onSubmit: handleSubmit(onSubmit) },
            React.createElement("div", { className: classNames(styles.sectionTopSpacer) },
                React.createElement(ErrorSummary, { ref: summaryRef, errorsCount: errorsCount, errors: errors })),
            React.createElement(SimpleQuestion, { form: form, competitionQuestion: competitionQuestion }),
            React.createElement(DetailsSection, { form: form }),
            React.createElement(AddressSection, { form: form }),
            React.createElement(TermsAndConditions, { form: form }),
            React.createElement(OptInSection, { form: form, optIns: optIns }),
            submitSection)));
};
CompetitionForm.propTypes = {
    competitionsSingleClickEntry: propTypes.bool.isRequired,
    userIsLoggedIn: propTypes.bool.isRequired,
    membershipCardNumber: propTypes.string,
    contactAddress: propTypes.shape({}),
    emailAddress: propTypes.string,
    breakpointClasses: propTypes.arrayOf(propTypes.string),
    competitionQuestion: propTypes.string,
    competitionEndDate: propTypes.string.isRequired,
    competitionId: propTypes.string.isRequired,
    competitionStatus: propTypes.string.isRequired,
    competitionType: propTypes.string.isRequired,
    onFormSubmit: propTypes.func.isRequired,
    questionAndOptions: propTypes.shape({
        question: propTypes.string,
        options: propTypes.arrayOf(propTypes.string),
    }),
    optIns: propTypes.shape({
        introText: propTypes.shape({
            data: propTypes.shape({
                analytics: analyticsPropType(),
                blocks: propTypes.arrayOf(richTextNode),
                componentId: propTypes.string,
                deviceBreakpoints: deviceBreakpointsProptypes(),
                noMargin: propTypes.bool,
                styleIds: propTypes.arrayOf(propTypes.string),
                valid: propTypes.bool,
            }),
            locationName: propTypes.string,
        }),
        optInList: propTypes.arrayOf(propTypes.shape({ optInName: propTypes.string, optInDescription: propTypes.string })),
    }),
    useGoogleReCaptcha: propTypes.func.isRequired,
};
export default CompetitionForm;
//# sourceMappingURL=CompetitionsForm.js.map